import { useState, useEffect, useMemo } from 'react';
import { subscribeCoachPortalUnreadCount } from '../services/portalMessagesService';
import { getTeamMessages } from '../firebase/coachFirestore';

/**
 * Parent / group rows in parent_messages that should show as unread in the coach inbox.
 */
export function countUnreadInboxTeamMessages(messages, coachId) {
  if (!messages?.length || !coachId) return 0;
  const idStr = String(coachId);
  return messages.filter((m) => {
    if (String(m.coachId || '') !== idStr) return false;
    if (m.read === true) return false;
    if (m.type === 'parent' || m.type === 'group') return true;
    if (!m.type && (m.parentName || m.parentEmail)) return true;
    return false;
  }).length;
}

/**
 * Accurate live totals: users/{coachId}/portalMessages unread + parent_messages inbox unread.
 * Subscribes to both; pass the same coach Firebase user id used in Firestore.
 */
export function useCoachInboxUnread(coachId) {
  const [portalUnread, setPortalUnread] = useState(0);
  const [teamMessages, setTeamMessages] = useState([]);

  useEffect(() => {
    if (!coachId) {
      setPortalUnread(0);
      return undefined;
    }
    return subscribeCoachPortalUnreadCount(coachId, setPortalUnread);
  }, [coachId]);

  useEffect(() => {
    if (!coachId) {
      setTeamMessages([]);
      return undefined;
    }
    return getTeamMessages(coachId, setTeamMessages);
  }, [coachId]);

  const teamUnread = useMemo(
    () => countUnreadInboxTeamMessages(teamMessages, coachId),
    [teamMessages, coachId]
  );

  const totalUnread = portalUnread + teamUnread;

  return {
    totalUnread,
    portalUnread,
    teamUnread,
    teamMessages,
  };
}
